import Link from '@liquidweb-dev/undercurrent/Link';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';

import { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';

import BrickWallFireIcon from '@liquidweb-dev/undercurrent/icons/lucide/BrickWallFire';
import EyeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Eye';
import HistoryIcon from '@liquidweb-dev/undercurrent/icons/lucide/History';
import ListChecksIcon from '@liquidweb-dev/undercurrent/icons/lucide/ListChecks';
import NetworkIcon from '@liquidweb-dev/undercurrent/icons/lucide/Network';
import CompleteSetUpImage from 'images/utm/complete-setup.png';

import DialogUTM from 'components/DialogUTM';

interface CompleteSetUpProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
}

interface TextIconProps {
	icon: React.ComponentType<LucideSvgProps>;
	label: string;
	linkText: string;
	linkURL: string;
}

const TextWithIcon = ({
	icon: Icon,
	label,
	linkText,
	linkURL,
}: TextIconProps) => {
	return (
		<Stack
			direction="row"
			sx={{
				alignItems: 'center',
				gap: 100,
			}}
		>
			<Icon
				sx={({ vars }) => ({
					color: vars.palette.uc.highlight.icon.main,
				})}
			/>
			<Stack
				direction="row"
				sx={[
					{
						alignItems: 'center',
						gap: 50,
					},
					({ vars }) => ({ color: vars.palette.uc.text.main }),
				]}
			>
				<Typography variant="body-md">{label}</Typography>
				<Link href={linkURL} target="_blank" rel="noopener" variant="body-md">
					{linkText}
				</Link>
			</Stack>
		</Stack>
	);
};

const CompleteSetUp = ({ onClose, ...dialogProps }: CompleteSetUpProps) => {
	return (
		<DialogUTM
			{...dialogProps}
			image={CompleteSetUpImage}
			badge={{
				icon: ListChecksIcon,
				label: 'Complete Your Setup',
			}}
			dialogContent={{
				title: 'Have you finished the essentials?',
				description: (
					<Stack
						sx={{
							gap: 200,
						}}
					>
						<Stack
							sx={{
								gap: 100,
							}}
						>
							<TextWithIcon
								icon={NetworkIcon}
								label="DNS"
								linkText="configured"
								linkURL="https://docs.nexcess.com/hosting/portal/domains-and-dns/dns-management/"
							/>
							<TextWithIcon
								icon={HistoryIcon}
								label="Backups"
								linkText="enabled"
								linkURL="https://docs.nexcess.com/hosting/server-administration/server-backup-and-restore/best-practices-developing-a-backup-plan/"
							/>
							<TextWithIcon
								icon={BrickWallFireIcon}
								label="Firewall"
								linkText="rules applied"
								linkURL="https://docs.nexcess.com/hosting/security/firewall-management/"
							/>
							<TextWithIcon
								icon={EyeIcon}
								label="Monitoring"
								linkText="reviewed"
								linkURL="https://docs.nexcess.com/hosting/portal/monitoring/server-monitoring-services/"
							/>
						</Stack>
						<Typography variant="body-md">
							Wrap up these steps to make sure your server is secure and
							reliable.
						</Typography>
					</Stack>
				),
			}}
			onClose={onClose}
		/>
	);
};

export default CompleteSetUp;
